import type { ReactNode } from "react";
import { BadgeCheck, Check, ChevronRight, type LucideIcon } from "lucide-react";
import {
  CLASSE_SELO_COMUM,
  CLASSE_SELO_GESTOR,
  CLASSE_SELO_LIDER,
  ROTULO_GESTOR_DE_ELO,
  rotuloDoGestor,
} from "@/core/utils/selosFuncao";

/**
 * Peças das ilustrações ESTÁTICAS da Ajuda do Elos: moldura, telas, botões e selos de mentira.
 * São desenhos: nada aqui é botão, link ou campo de verdade (não recebem foco nem toque), e usam
 * só os tokens de cor e os ícones lucide do Elos.
 */

export const DESTAQUE = "ring-2 ring-primary ring-offset-2 ring-offset-bg";

export function Moldura({ descricao, children }: { descricao: string; children: ReactNode }) {
  return (
    <figure
      role="img"
      aria-label={descricao}
      className="pointer-events-none m-0 select-none overflow-hidden rounded-2xl border border-border bg-bg"
    >
      <figcaption className="px-3 pt-2 text-[11px] font-bold uppercase tracking-wide text-muted">
        Exemplo
      </figcaption>
      <div aria-hidden="true" className="p-3">
        {children}
      </div>
    </figure>
  );
}

/** Várias peças dentro da mesma moldura, com respiro entre elas. */
export function Pilha({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2.5">{children}</div>;
}

export function Tela({ children }: { children: ReactNode }) {
  return <div className="overflow-hidden rounded-xl border border-border bg-bg">{children}</div>;
}

export function Interruptor({ ligado }: { ligado: boolean }) {
  return (
    <span
      className={`relative h-6 w-11 shrink-0 rounded-full ${ligado ? "bg-primary" : "bg-border"}`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface ${
          ligado ? "left-0.5 translate-x-5" : "left-0.5"
        }`}
      />
    </span>
  );
}

export function ItemAviso({
  icone: Icone,
  texto,
  quando,
  lido = false,
}: {
  icone: LucideIcon;
  texto: string;
  quando: string;
  lido?: boolean;
}) {
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border border-border bg-surface p-3 ${
        lido ? "opacity-60" : ""
      }`}
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
        <Icone size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-text">{texto}</span>
        <span className="block text-xs text-muted">{quando}</span>
      </span>
      {!lido && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />}
    </div>
  );
}

/* ---------- Peças das lições de ministério, liderança e elos ---------- */

type TomCartao = "padrao" | "convite" | "ok" | "info";

const TONS_CARTAO: Record<TomCartao, string> = {
  padrao: "border-border bg-surface",
  convite: "border-primary/40 bg-primary/5",
  ok: "border-success/50 bg-success/10",
  info: "border-info/50 bg-info/10",
};

export function Cartao({
  children,
  tom = "padrao",
  folga = "p-3",
}: {
  children: ReactNode;
  tom?: TomCartao;
  folga?: string;
}) {
  return <div className={`rounded-xl border ${TONS_CARTAO[tom]} ${folga}`}>{children}</div>;
}

export function CabecalhoCartao({ titulo, direita }: { titulo: string; direita?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <span className="min-w-0 text-sm font-extrabold text-text">{titulo}</span>
      {direita}
    </div>
  );
}

export function Texto({ children, pequeno = false }: { children: ReactNode; pequeno?: boolean }) {
  return (
    <p className={`m-0 leading-snug text-text ${pequeno ? "text-xs" : "text-[13px]"}`}>{children}</p>
  );
}

export function Sub({ children }: { children: ReactNode }) {
  return <span className="block text-xs text-muted">{children}</span>;
}

/** Botão de mentira. `destacado` é o que a lição manda tocar. */
export function MiniBotao({
  rotulo,
  icone: Icone,
  secundario = false,
  destacado = false,
  cheio = false,
}: {
  rotulo: string;
  icone?: LucideIcon;
  secundario?: boolean;
  destacado?: boolean;
  cheio?: boolean;
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold ${
        secundario ? "border border-border bg-surface text-text" : "bg-primary text-on-primary"
      } ${cheio ? "w-full py-2" : ""} ${destacado ? DESTAQUE : ""}`}
    >
      {Icone && <Icone size={13} />}
      {rotulo}
    </span>
  );
}

type TomPilula = "marca" | "espera" | "ok" | "info";

const TONS_PILULA: Record<TomPilula, string> = {
  marca: "bg-primary/10 text-primary",
  espera: "bg-warning/10 text-warning",
  ok: "bg-success/10 text-success",
  info: CLASSE_SELO_GESTOR,
};

export function Pilula({
  tom = "marca",
  destacada = false,
  children,
}: {
  tom?: TomPilula;
  destacada?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${TONS_PILULA[tom]} ${
        destacada ? DESTAQUE : ""
      }`}
    >
      {children}
    </span>
  );
}

/** Linha de lista: nome, uma linha de apoio e, à direita, o que a lição quiser mostrar. */
export function Linha({
  nome,
  apoio,
  direita,
}: {
  nome: ReactNode;
  apoio?: ReactNode;
  direita?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-t border-border py-2 first:border-t-0 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1">
        <span className="block break-words text-sm font-bold text-text">{nome}</span>
        {apoio && <span className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted">{apoio}</span>}
      </div>
      {direita && <div className="flex shrink-0 items-center gap-1.5">{direita}</div>}
    </div>
  );
}

export function Campo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="mt-2">
      <span className="block text-xs font-bold text-text">{rotulo}</span>
      <span className="mt-1 block rounded-lg border border-border bg-bg px-2.5 py-2 text-xs text-muted">
        {valor}
      </span>
    </div>
  );
}

/** Opção com chave de liga e desliga e uma frase de apoio. */
export function Chave({
  titulo,
  apoio,
  ligada,
  destacada = false,
}: {
  titulo: string;
  apoio: string;
  ligada: boolean;
  destacada?: boolean;
}) {
  return (
    <div
      className={`mt-2 flex items-center justify-between gap-3 rounded-lg border border-border bg-bg p-2.5 ${
        destacada ? DESTAQUE : ""
      }`}
    >
      <span className="min-w-0">
        <span className="block text-xs font-bold text-text">{titulo}</span>
        <span className="block text-[11px] text-muted">{apoio}</span>
      </span>
      <Interruptor ligado={ligada} />
    </div>
  );
}

/** Opção de marcar (a caixinha do formulário de função). */
export function Marcador({
  titulo,
  apoio,
  marcada = false,
  destacada = false,
}: {
  titulo: string;
  apoio: string;
  marcada?: boolean;
  destacada?: boolean;
}) {
  return (
    <div
      className={`mt-2 flex items-start gap-2 rounded-lg border border-border bg-bg p-2.5 ${
        destacada ? DESTAQUE : ""
      }`}
    >
      <span
        className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border ${
          marcada ? "border-primary bg-primary text-on-primary" : "border-border bg-surface"
        }`}
      >
        {marcada && <Check size={11} />}
      </span>
      <span className="min-w-0">
        <span className="block text-xs font-bold text-text">{titulo}</span>
        <span className="block text-[11px] leading-snug text-muted">{apoio}</span>
      </span>
    </div>
  );
}

/** Caixa de aviso dentro da tela (conflito, confirmação enviada, ambiente reservado). */
export function Faixa({
  icone: Icone,
  tom = "ok",
  children,
}: {
  icone?: LucideIcon;
  tom?: "ok" | "info" | "alerta";
  children: ReactNode;
}) {
  const classe =
    tom === "alerta"
      ? "border-warning/50 bg-warning/10"
      : tom === "info"
        ? "border-info/50 bg-info/10"
        : "border-success/50 bg-success/10";
  return (
    <div className={`mt-2 flex items-start gap-2 rounded-lg border p-2.5 text-xs text-text ${classe}`}>
      {Icone && <Icone size={15} className="mt-0.5 shrink-0" />}
      <span className="min-w-0 leading-snug">{children}</span>
    </div>
  );
}

/** Quadro "o que se pode fazer": ícone, título e uma linha de apoio por item. */
export function Poderes({
  itens,
}: {
  itens: { icone: LucideIcon; titulo: string; apoio: string }[];
}) {
  return (
    <Cartao>
      {itens.map(({ icone: Icone, titulo, apoio }) => (
        <div
          key={titulo}
          className="flex items-center gap-3 border-t border-border py-2 first:border-t-0 first:pt-0 last:pb-0"
        >
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Icone size={18} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-text">{titulo}</span>
            <span className="block text-xs text-muted">{apoio}</span>
          </span>
        </div>
      ))}
    </Cartao>
  );
}

/** Link "Gerenciar ›", "Ver como usar ›" do desenho. */
export function LinkFalso({ rotulo, destacado = false }: { rotulo: string; destacado?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-md text-sm font-bold text-primary dark:text-text ${
        destacado ? DESTAQUE : ""
      }`}
    >
      {rotulo}
      <ChevronRight size={15} />
    </span>
  );
}

/* ---------- Selos (os mesmos da tela do ministério) ---------- */

const SELO_BASE = "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold";

function Destaque({ ligado, children }: { ligado: boolean; children: ReactNode }) {
  return ligado ? <span className={`inline-flex rounded-full ${DESTAQUE}`}>{children}</span> : <>{children}</>;
}

export function SeloLider({ destacado = false }: { destacado?: boolean }) {
  return (
    <Destaque ligado={destacado}>
      <span className={`${SELO_BASE} ${CLASSE_SELO_LIDER}`}>
        <span aria-hidden="true" style={{ fontSize: 10, lineHeight: 1 }}>
          🗣️
        </span>
        Líder do Ministério
      </span>
    </Destaque>
  );
}

export function SeloGestor({
  funcao,
  destacado = false,
}: {
  funcao?: string;
  destacado?: boolean;
}) {
  return (
    <Destaque ligado={destacado}>
      <span className={`${SELO_BASE} ${CLASSE_SELO_GESTOR}`}>
        <BadgeCheck size={10} />
        {funcao ? rotuloDoGestor(funcao) : ROTULO_GESTOR_DE_ELO}
      </span>
    </Destaque>
  );
}

export function SeloFuncao({ texto, destacado = false }: { texto: string; destacado?: boolean }) {
  return (
    <Destaque ligado={destacado}>
      <span className={`${SELO_BASE} ${CLASSE_SELO_COMUM}`}>{texto}</span>
    </Destaque>
  );
}
